import userService from "./userService";
import testOrderService from "./testOrderService";
import testService from "./testService";
import analyticsService from "./analyticsService";

const dashboardService = {
  // Get comprehensive dashboard statistics
  getDashboardStats: async () => {
    try {
      const [usersRes, testOrdersRes, testsRes] = await Promise.all([
        userService.getAllUsers(),
        testOrderService.getAllTestOrders(),
        testService.getAllTests(),
      ]);

      // Normalize various API response shapes into arrays (no mock data)
      const toArray = (res, preferredKeys = []) => {
        if (Array.isArray(res)) return res;
        const obj = res?.data ?? res;
        if (Array.isArray(obj)) return obj;
        for (const k of [...preferredKeys, "data", "results", "items", "rows", "list", "users", "tests", "test_orders", "testOrders", "orders"]) {
          const v = obj?.[k];
          if (Array.isArray(v)) return v;
        }
        if (obj && typeof obj === "object") {
          for (const v of Object.values(obj)) {
            if (Array.isArray(v)) return v;
          }
        }
        return [];
      };

      const users = toArray(usersRes, ["users"]);
      const testOrders = toArray(testOrdersRes, ["test_orders", "orders", "testOrders"]);
      const tests = toArray(testsRes, ["tests"]);

      // Calculate statistics
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const todayOrders = testOrders.filter(order => {
        const orderDate = new Date(order.created_at);
        return orderDate >= today;
      });

      const pendingOrders = testOrders.filter(order => 
        order.status === 'pending' || order.status === 'in_progress'
      );

      const completedOrders = testOrders.filter(order => 
        order.status === 'completed'
      );

      const urgentOrders = pendingOrders.filter(order => 
        order.priority === 'urgent' || order.priority === 'high'
      );

      // User statistics by role
      const usersByRole = users.reduce((acc, user) => {
        acc[user.role] = (acc[user.role] || 0) + 1;
        return acc;
      }, {});

      // Test orders by status
      const ordersByStatus = testOrders.reduce((acc, order) => {
        acc[order.status] = (acc[order.status] || 0) + 1;
        return acc;
      }, {});

      // Recent activity (last 7 days)
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      
      const recentOrders = testOrders.filter(order => {
        const orderDate = new Date(order.created_at);
        return orderDate >= weekAgo;
      });

      // Daily orders for the last 7 days
      const dailyOrders = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        date.setHours(0, 0, 0, 0);
        const nextDate = new Date(date);
        nextDate.setDate(nextDate.getDate() + 1);

        const dayOrders = testOrders.filter(order => {
          const orderDate = new Date(order.created_at);
          return orderDate >= date && orderDate < nextDate;
        });

        dailyOrders.push({
          date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          orders: dayOrders.length,
          completed: dayOrders.filter(o => o.status === 'completed').length,
          pending: dayOrders.filter(o => o.status === 'pending' || o.status === 'in_progress').length,
        });
      }

      return {
        totalUsers: users.length,
        totalTests: tests.length,
        todayOrders: todayOrders.length,
        pendingOrders: pendingOrders.length,
        completedOrders: completedOrders.length,
        urgentOrders: urgentOrders.length,
        usersByRole,
        ordersByStatus,
        recentOrders: recentOrders.length,
        dailyOrders,
        completionRate: testOrders.length > 0 
          ? Math.round((completedOrders.length / testOrders.length) * 100) 
          : 0,
      };
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
      throw error;
    }
  },

  // Get analytics summary with date range
  getAnalyticsSummary: async (from, to, granularity = 'daily') => {
    try {
      const response = await analyticsService.getSummary({ from, to, granularity });
      return response.data.data;
    } catch (error) {
      console.error("Error fetching analytics summary:", error);
      throw error;
    }
  },
};

export default dashboardService;
